import { useContext, useEffect, useCallback, useRef } from 'react';
import { defineMessages } from 'react-intl';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';

import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { fg } from '@confluence/feature-gating';
import {
	useObjectSidebarActions,
	usePanel,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { useAnnotationsDispatch } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useCommentsPanelActions,
	type ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { usePageContentId } from '@confluence/page-context';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import {
	isSpaceOverview,
	isDatabase,
	isWhiteboard,
	isSlides,
} from '@confluence/named-routes/entry-points/routes-utils.ts';
import { SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import {
	useCommentsContentActions,
	useCommentsContentState,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import type { PanelProps } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import type { CommentsPanelWrapperProps } from './CommentsPanelWrapper.tsx';

const CommentsPanelFilterMenu = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFilterMenu" */ './CommentsPanelFilterMenu.tsx'
			)
		).CommentsPanelFilterMenu,
});

const CommentsPanelFooter = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFooter" */ './CommentsPanelFooter.tsx'
			)
		).CommentsPanelFooter,
});

const CommentsPanelHeaderIcon = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelHeaderIcon" */ './CommentsPanelHeaderIcon.tsx'
			)
		).CommentsPanelHeaderIcon,
});

const CommentsPanelWrapper = LoadableLazy<CommentsPanelWrapperProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelWrapper" */ './CommentsPanelWrapper.tsx'
			)
		).CommentsPanelWrapper,
});

const CommentsPanelFooterWrapper = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFooterWrapper" */ './CommentsPanelFooterWrapper.tsx'
			)
		).CommentsPanelFooterWrapper,
});

const CommentsPanelFilterMenuWrapper = LoadableLazy<PanelProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentsPanelFilterMenuWrapper" */ './CommentsPanelFilterMenuWrapper.tsx'
			)
		).CommentsPanelFilterMenuWrapper,
});

export const i18n = defineMessages({
	commentsPanelHeaderLabel: {
		id: 'comments-panel.panel.header.label',
		defaultMessage: 'Comments',
		description:
			'The text is shown as the header label at the top of the comments panel sidebar, identifying the panel to the user.',
	},
	discussionsPanelHeaderLabel: {
		id: 'comments-panel.panel.header.discussions.label',
		defaultMessage: 'Discussions',
		description: 'Label for the comments panel header when the AI suggestions tab is enabled',
	},
});

export const preloadCommentsPanel = (pageMode: PageMode) => {
	// Always preload the core components
	CommentsPanelWrapper.preload();
	CommentsPanelFilterMenu.preload();
	CommentsPanelHeaderIcon.preload();

	// Only preload footer in view mode (not edit mode)
	if (pageMode !== PageMode.EDIT) {
		CommentsPanelFooter.preload();
	}
};

export const useShowCommentsPanel = () => {
	const isSpaceOverviewPage = isSpaceOverview();
	const isDatabaseView = isDatabase();
	const isWhiteboardView = isWhiteboard();
	const isSlidesView = isSlides();
	const { contentStatus } = useContentStatus();

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();
	const { showObjectSidebar, hideObjectSidebar } = useObjectSidebarActions();

	const { setViewProps, setEditorViewProps } = useAnnotationsDispatch();

	const { setCurrentView, setCurrentlySelectedThreadKey, setReplyToCommentId } =
		useCommentsPanelActions();
	const [contentId] = usePageContentId();
	const { canAddComments } = useAddCommentPermissionCheck(contentId ?? '', true);

	const pageMode = useGetPageMode();
	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { showModal } = useDialogs();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();

	const allowComments = useEmbeddedComments();

	// a ref is needed here because of a closure issue with canClosePanel
	const hasContentChangedRef = useRef(hasContentChanged);
	useEffect(() => {
		hasContentChangedRef.current = hasContentChanged;
	}, [hasContentChanged]);

	const onPanelCloseFunction = () => {
		setCurrentView(undefined);

		// Unselect active annotations on the page
		if (pageMode === PageMode.VIEW) {
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			setViewProps(null);
		} else {
			eventEmitter.emit('setselectedannotation', '');
			setEditorViewProps(null);
		}

		// clear selected comment thread in the comments panel
		setCurrentlySelectedThreadKey('');
		setReplyToCommentId();
	};

	const onPanelCloseCallback = useCallback(onPanelCloseFunction, [
		setCurrentView,
		pageMode,
		eventEmitter,
		setViewProps,
		setEditorViewProps,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
	]);

	const onPanelClose = expValEquals(
		'confluence_edit_page_rerender_optimizations',
		'isEnabled',
		true,
	)
		? onPanelCloseCallback
		: onPanelCloseFunction;

	const canClosePanel = useCallback(async () => {
		if (hasContentChangedRef.current && !fg('confluence_comments_fix_duplicate_modal')) {
			return new Promise<boolean>((resolve) => {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged && resetContentChanged();
						resolve(true);
					},
					onCancel: () => {
						resolve(false);
					},
				});
			});
		} else {
			return true;
		}
	}, [resetContentChanged, showModal]);

	const showCommentsPanelFunction = ({ openWithView }: { openWithView?: ViewValues } = {}) => {
		if (!allowComments) {
			return;
		}

		experienceTracker.start({
			name: SIDEBAR_COMMENTS_PANEL_LOAD_EXPERIENCE,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				openWithView,
				contentId,
				pageMode,
			},
		});

		const loadFooterComments = pageMode !== PageMode.EDIT;

		// Drag-to-resize on page surfaces only (same exclusions as the panel
		// itself). `expValEquals` is the LAST clause per
		// `confluence-feature-gating/no-preconditioning` so exposure is only
		// recorded for users who actually meet the surface preconditions.
		const isResizableSurface =
			!isSpaceOverviewPage &&
			!isDatabaseView &&
			!isWhiteboardView &&
			!isSlidesView &&
			expValEquals('confluence_fe_comments_panel_resize', 'isEnabled', true);

		const showFooter =
			canAddComments &&
			contentStatus !== ContentStatus.DRAFT &&
			loadFooterComments &&
			!isSpaceOverviewPage &&
			!isDatabaseView &&
			!isWhiteboardView &&
			!isSlidesView;

		// When the gate is on, use CommentsPanelFooterWrapper which reactively
		// switches between SuggestionsFooter and CommentsPanelFooter based on
		// the current tab — without needing to re-open the sidebar.
		// When the gate is off, fall back to the original CommentsPanelFooter.
		const FooterComponent = expValEquals(
			'platform_editor_ai_content_suggested_edits',
			'isEnabled',
			true,
		)
			? CommentsPanelFooterWrapper
			: showFooter
				? CommentsPanelFooter
				: undefined;

		showObjectSidebar(
			{
				id: PanelName.CommentsPanel,
				headerComponentElements: {
					HeaderIcon: CommentsPanelHeaderIcon,
					headerLabel: expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true)
						? i18n.discussionsPanelHeaderLabel
						: i18n.commentsPanelHeaderLabel,
					HeaderRightAlignedElement: expValEquals(
						'platform_editor_ai_content_suggested_edits',
						'isEnabled',
						true,
					)
						? CommentsPanelFilterMenuWrapper
						: CommentsPanelFilterMenu,
				},
				BodyComponent: () => CommentsPanelWrapper({ openWithView }),
				FooterComponent,
				closeOptions: {
					canClosePanel,
					onPanelClose,
				},
				isResizable: isResizableSurface,
			},
			'push',
		);
	};

	const showCommentsPanelHandler = useCallback(showCommentsPanelFunction, [
		allowComments,
		canAddComments,
		canClosePanel,
		contentId,
		contentStatus,
		experienceTracker,
		isDatabaseView,
		isSpaceOverviewPage,
		isWhiteboardView,
		isSlidesView,
		onPanelClose,
		pageMode,
		showObjectSidebar,
	]);

	const showCommentsPanel = expValEquals(
		'confluence_edit_page_rerender_optimizations',
		'isEnabled',
		true,
	)
		? showCommentsPanelHandler
		: showCommentsPanelFunction;

	// NOTE: Exposing the hide function for now while we have the CommentButton
	return {
		isCommentsPanelShown: isObjectSidebarShown && panel?.id === PanelName.CommentsPanel,
		showCommentsPanel,
		hideCommentsPanel: hideObjectSidebar,
	};
};
